import type {PortfoliosState} from '../../types'

import {
  append, assoc, compose, contains, dissoc, ifElse, lensPath,
  lensProp, map, over, prop, merge, set, pipe,
} from 'ramda'
import moment from 'moment'
import {replace, toIdMapBy, toIndexerMapBy, checkStateOfEventRules, toSafeNumberIdMapBy} from '@/store/util'
import fileDownloader from '@/file-downloader'
import {prependIndexerString, prependRulesIndexerString} from './util'
import {formRules} from '../../rules-helper'
const initialState: PortfoliosState = {}
const portfolioPath = (id: ?number) =>
  lensProp(prependIndexerString(id))

const portfolioCompaniesPath = (id: ?number) =>
  compose(portfolioPath(id), lensProp('companies'))

const portfolioScheduledExportsPath = (id: ?number) =>
  compose(portfolioPath(id), lensPath(['exports', 'scheduled']))

const portfolioExportPathByIndexer = (id: ?number, exportId: ?number) =>
  compose(portfolioScheduledExportsPath(id), lensProp(prependIndexerString(exportId)))

const portfolioExportsPath = (id: ?number) =>
  compose(portfolioPath(id), lensProp('exports'))

const getCountry = (country: string) => ifElse(
  contains(country),
  (x) => x,
  append(country),
)
const companiesForStore = toSafeNumberIdMapBy('safeNumber')

const scheduledExportsForStore = toIndexerMapBy('exportId')
const previousExportsForStore = toIndexerMapBy('historyId')

const portfoliosForStore = toIndexerMapBy('portfolioId')

const portfolioCompanyPath = (portfolioId: ?number, companyId: string) =>
  compose(portfolioCompaniesPath(portfolioId), lensProp(companyId))

const toGraphData = ({startDate, endDate, data}) => {
  const values = []
  if (data) {
    const existing = toIdMapBy('dataDate')(data)
    let date = moment(startDate).startOf('day')

    while (date.isBefore(moment(endDate))) {
      const k = date.format('YYYY-MM-DDTHH:mm:ss')
      const key = moment(k).format('DD MMM YYYY')

      if (existing[k]) {
        values.push({
          key,
          value: existing[k].dataValue,
        })
      } else {
        values.push({
          key,
          value: 0,
        })
      }
      date.add(1, 'day')
    }
  }
  return values
}

const composeInitialData = (id, data) => (state) => {
  return over(portfolioPath(id), replace(data), state)
}
const initialSelectedCompanies = {
  isAll: false,
  data: [],
  totalCompanies: 0,
}
export default (state: PortfoliosState = initialState, action): PortfoliosState => {
  const {type, payload} = action
  switch (type) {
    case 'FETCH_INITIAL_DATA_SUCCESS':
      if (payload.portfoliosObj && payload.portfoliosObj.portfolios.length > 0) {
        return portfoliosForStore(payload.portfoliosObj.portfolios)
      }
      return state
    case 'GET_PORTFOLIOS_DATA_SUCCESS':
      return portfoliosForStore(payload.portfoliosObj.portfolios)
    case 'ADD_PORTFOLIO_SUCCESS':
      const portfolios = action.portfolioData.isDefault
        ? map(assoc('isDefault', false))(state)
        : state
      const portfolioIndexer = prependIndexerString(action.activePortfolioId)
      const activePortfolio = prop(portfolioIndexer)(portfolios)
      const newPortfolios = portfoliosForStore(payload.portfolios)
      return over(
        portfolioPath(action.activePortfolioId),
        replace(activePortfolio),
        newPortfolios
      )
    case 'GET_PORTFOLIO_DATA_SUCCESS':
      const activePortfolioIndexer = prependIndexerString(action.portfolioId)
      const activePortfolioData = prop(activePortfolioIndexer)(state)
      const mergePortfolio = {...activePortfolioData, ...payload}
      return over(
        portfolioPath(action.portfolioId),
        replace(mergePortfolio),
        state
      )
    case 'DELETE_PORTFOLIO_SUCCESS':
      return dissoc(prependIndexerString(action.portfolioId))(state)
    case 'UPDATE_PORTFOLIO_SUCCESS':
      const portfoliosMap = action.portfolioData.isDefault
        ? map(assoc('isDefault', false))(state)
        : state
      const portfolioIndex = prependIndexerString(action.portfolioId)
      const currentPortfolio = prop(portfolioIndex)(portfoliosMap)
      const latestPortfolios = portfoliosForStore(payload.portfolios)
      const mergedPortfolioData = merge(currentPortfolio, latestPortfolios[portfolioIndex])
      return over(
        portfolioPath(action.portfolioId),
        replace(mergedPortfolioData),
        latestPortfolios
      )
    case 'UPDATE_COMPANY_DETAILS_SUCCESS':
      const company =
        portfolioCompanyPath(action.activePortfolioId, action.companyId)
      return over(
        company,
        replace(action.companyData),
        state,
      )
    case 'FETCH_PORTFOLIO_EVENTS_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          notificationEvents: payload.data,
          notificationEventsCount: payload.totalCount,
          filteredNotificationEvents: null,
        }),
        state
      )
    case 'RESET_MODAL_EVENTS_DATA':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          modalEventsCount: 0,
          modalEvents: [],
        }),
        state,
      )
    case 'FETCH_PORTFOLIO_MODAL_EVENTS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          modalEventsLoader: true,
        }),
        state,
      )
    case 'FETCH_PORTFOLIO_MODAL_EVENTS_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          modalEventsCount: payload.totalCount,
          modalEvents: payload.data,
          modalEventsLoader: false,
        }),
        state,
      )
    case 'FETCH_PORTFOLIO_MODAL_EVENTS_FAILURE':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          modalEventsCount: 0,
          modalEvents: [],
          modalEventsLoader: false,
        }),
        state,
      )
    case 'DELETE_EXPORT_SUCCESS':
      return over(
        portfolioScheduledExportsPath(action.portfolioId),
        dissoc(prependIndexerString(action.exportId)),
        state,
      )
    case 'ADD_EXPORT_SUCCESS':
      if (action.activePortfolioId === action.formData.portfolioId) {
        action.formData.exportId = action.payload.data.exportId
        const indexer = prependIndexerString(action.formData.exportId)
        return over(
          portfolioScheduledExportsPath(action.formData.portfolioId),
          assoc(indexer, action.formData),
          state
        )
      }
      return state
    case 'SAVE_EXPORT_SUCCESS':
      let data = action.formData
      if (data.exportType === 'Immediate') {
        data.nextExportDate = ''
      }
      return over(
        portfolioExportPathByIndexer(action.formData.portfolioId, action.exportId),
        replace(data),
        state
      )
    case 'FETCH_EXPORT_ATTACHMENT_SUCCESS':
      fileDownloader(action.payload)('exports')
      return state
    case 'FETCH_SCHEDULED_EXPORTS_SUCCESS':
      return over(
        portfolioExportsPath(action.portfolioId),
        replace({
          scheduled: scheduledExportsForStore(payload.data || []),
          scheduledCount: payload.totalCount,
        }),
        state
      )
    case 'FETCH_PREVIOUS_EXPORTS_SUCCESS':
      return over(
        portfolioExportsPath(action.portfolioId),
        replace({
          previous: previousExportsForStore(payload.data || []),
          previousCount: payload.totalCount,
        }),
        state
      )
    case 'FETCH_PORTFOLIO_COMPANIES_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          companiesCount: payload.totalCount,
          companies: companiesForStore(payload.data, action.portfolioId),
        }),
        state
      )
    case 'FETCH_PORTFOLIO_COMPANIES_BY_RISK_TYPE_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          companiesByRiskTypeCount: payload.totalCount,
          companiesByRiskType: companiesForStore(payload.data, action.portfolioId),
        }),
        state
      )
    case 'EDIT_EVENT_RULE':
      const {change, country, groupId, portfolioId} = action
      const groupIndexer = prependRulesIndexerString('group', groupId)
      const ruleIndexer = prependRulesIndexerString('rule', change.ruleCode)
      const portfIndexer = prependIndexerString(portfolioId)
      let changePath = []
      const addCountry = getCountry(country)
      if (change.check) {
        const updateRulesState = checkStateOfEventRules(action, portfIndexer)
        return (pipe(
          set(lensPath(updateRulesState.changePath), change.val),
          (updateRulesState.totalCount < 1) ? set(lensPath(updateRulesState.changeAllPath), 1) : set(lensPath(updateRulesState.changeAllPath), 0),
          over(lensPath([portfIndexer, 'ruleCountriesChanged']), addCountry),
        )(state)
        )
      } else {
        changePath = [
          portfIndexer, 'rules', country, groupIndexer, 'notificationRules', ruleIndexer, change.prop,
        ]
        return pipe(
          set(lensPath(changePath), change.val),
          over(lensPath([portfIndexer, 'ruleCountriesChanged']), addCountry),
        )(state)
      }
    case 'UPDATE_GRAPH_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({graphData: toGraphData(action.payload || {})}),
        state,
      )
    case 'FETCH_HOME_TAB_DATA_SUCCESS':
      if (payload.notificationEvents) {
        const obj = {
          notificationEventsCount: payload.notificationEvents.totalCount || 0,
          notificationEvents: payload.notificationEvents.data || [],
          filteredNotificationEvents: null,
        }
        return composeInitialData(action.portfolioId, obj)(state)
      }
      return state
    case 'FILTERED_NOTIFICATIONS_DATA':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          filteredNotificationEvents: action.filteredData,
        }),
        state
      )
    case 'FETCH_PORTFOLIOS_TAB_DATA_SUCCESS':
      const portfoliosTabData = {
        companiesCount: payload.companies ? payload.companies.totalCount : 0,
        companies: companiesForStore(payload.companies ? (payload.companies.data || []) : [], action.portfolioId),
        countries: payload.countries || [],
        selectedCompanies: {
          ...initialSelectedCompanies,
          totalCompanies: payload.companies ? payload.companies.totalCount : 0,
        },
      }
      return composeInitialData(action.portfolioId, portfoliosTabData)(state)
    case 'FETCH_SETTINGS_TAB_DATA_SUCCESS':
      const resultSet = formRules(payload.rules)
      const settingsTabData = {
        rules: resultSet,
        ruleCountriesChanged: [],
      }
      return composeInitialData(action.portfolioId, settingsTabData)(state)
    case 'FETCH_EXPORTS_TAB_DATA_SUCCESS':
      const exportsTabData = {
        exports: {
          scheduled: scheduledExportsForStore(payload.scheduled ? (payload.scheduled.data || []) : []),
          previous: previousExportsForStore(payload.previous ? (payload.previous.data || []) : []),
          previousCount: payload.previous ? payload.previous.totalCount : 0,
          scheduledCount: payload.scheduled ? payload.scheduled.totalCount : 0,
        },
      }
      return composeInitialData(action.portfolioId, exportsTabData)(state)
    case 'FETCH_ANALYSIS_TAB_DATA_SUCCESS':
      const analysisTabData = {
        riskSummary: payload.riskSummary,
        companiesByRiskType: companiesForStore(payload.companiesByRiskType
          ? (payload.companiesByRiskType.data || []) : [],
        action.portfolioId),
        companiesByRiskTypeCount: payload.companiesByRiskType ? payload.companiesByRiskType.totalCount : 0,
      }
      return composeInitialData(action.portfolioId, analysisTabData)(state)
    case 'FETCH_COMMON_TAB_DATA_SUCCESS':
      const commonTabData = {
        'notificationEventsSummary': payload.notificationEventsSummary || {},
      }
      return composeInitialData(action.portfolioId, commonTabData)(state)
    case 'UPDATE_SELECTED_COMPANIES':
      const prevStateOfCurrentPortfolio = state[prependIndexerString(action.portfolioId)]
      return over(
        portfolioPath(action.portfolioId),
        replace({
          selectedCompanies: {
            ...prevStateOfCurrentPortfolio.selectedCompanies,
            ...payload.list,
          },
        }),
        state,
      )
    case 'CHANGE_ACTIVE_PORTFOLIO':
      return over(
        portfolioPath(action.prevPortfolioId),
        replace({
          selectedCompanies: initialSelectedCompanies,
        }),
        state,
      )
    case 'IMPORT_COMPANIES_FILE_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          selectedCompanies: initialSelectedCompanies,
        }),
        state,
      )
    case 'SYNC_COMPANIES_FILE_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          selectedCompanies: initialSelectedCompanies,
        }),
        state,
      )
    case 'COPY_PORTFOLIO_COMPANIES_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          selectedCompanies: initialSelectedCompanies,
        }),
        state,
      )
    case 'COPY_PORTFOLIO_COMPANIES_FAILURE':
      return state
    case 'MOVE_PORTFOLIO_COMPANIES_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          selectedCompanies: initialSelectedCompanies,
        }),
        state,
      )
    case 'MOVE_PORTFOLIO_COMPANIES_FAILURE':
      return state
    case 'GET_PORTFOLIOS_COUNTRIES_SUCCESS':
      return over(
        portfolioPath(action.portfolioId),
        replace({
          countries: payload.countries || [],
        }),
        state,
      )
    case 'SAVE_LANG_PREFERENCE_SUCCESS':
      return (
        over(
          portfolioPath(action.portfolioId),
          replace({
            companiesCount: payload.companies.totalCount,
            companies: companiesForStore(payload.companies.data, action.portfolioId),
            companiesByRiskTypeCount: payload.companies.totalCount,
            companiesByRiskType: companiesForStore(payload.companies.data, action.portfolioId),
          }),
          state
        )
      )
    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/store/portfolios/reducer.js